import { encodePcmToWavBase64 } from './pcmUtils';

export interface VadHandlers {
  onSpeechStart: () => void;
  onSpeechEnd: (audio: Float32Array) => void;
}

export interface VadBackend {
  init(handlers: VadHandlers): Promise<void>;
  start(): void;
  pause(): void;
  stop(): Promise<void>;
}

export interface VadRunnerOptions {
  backend: VadBackend;
  sampleRate: number;
  onSpeechStart: () => void;
  onSegment: (wavBase64: string) => void;
}

export class VadRunner {
  constructor(private opts: VadRunnerOptions) {}

  async init(): Promise<void> {
    await this.opts.backend.init({
      onSpeechStart: () => {
        console.info('[Phase4a/VAD] speech start');
        this.opts.onSpeechStart();
      },
      onSpeechEnd: (audio) => {
        console.info('[Phase4a/VAD] speech end, samples=', audio.length);
        this.opts.onSegment(encodePcmToWavBase64(audio, this.opts.sampleRate));
      },
    });
  }

  start(): void {
    this.opts.backend.start();
  }

  pause(): void {
    this.opts.backend.pause();
  }

  async stop(): Promise<void> {
    await this.opts.backend.stop();
  }
}

/**
 * Production backend using @ricky0123/vad-web.
 * Lazy-imported to avoid loading ONNX runtime in unit tests.
 */
export async function createBrowserSileroBackend(): Promise<VadBackend> {
  const { MicVAD } = await import('@ricky0123/vad-web');
  let micvad: Awaited<ReturnType<typeof MicVAD.new>> | null = null;
  // Guards against two hazards:
  //  - double init (React StrictMode double-mount) would leak the previous
  //    microphone stream without calling destroy()
  //  - stop() called while init()'s MicVAD.new is still in flight would
  //    leave a live instance with no handle to destroy
  let destroyed = false;

  return {
    async init(handlers) {
      if (micvad) {
        micvad.destroy();
        micvad = null;
      }
      const instance = await MicVAD.new({
        // Serve all model + worklet + ort wasm assets from our own origin
        // (public/vad/) — default points at jsdelivr CDN which is blocked
        // by our CSP `connect-src 'self'`. scripts/copy-vad-assets.sh keeps
        // public/vad/ in sync on postinstall.
        baseAssetPath: '/vad/',
        onnxWASMBasePath: '/vad/',
        // Use vad-web's v5 code path with the bundled Silero v5 weights.
        // (Tried v6.2.1 weights — same ONNX I/O — but vad-web's v5 code
        // path didn't pick up speech, almost certainly a probability
        // calibration shift. Revisit when vad-web adds explicit v6 support.)
        model: 'v5',
        // Force ORT 1.14.0 into single-threaded, in-process mode. Default
        // behavior spawns a helper Web Worker + threaded wasm, but the
        // pinned ORT 1.14 minified worker bundle crashes with
        // `ReferenceError: g is not defined` under our CSP. Silero VAD
        // runs trivially on the main thread (tiny model, 512-sample frames).
        ortConfig: (ort) => {
          ort.env.wasm.numThreads = 1;
          ort.env.wasm.proxy = false;
        },
        // Values below are the @ricky0123/vad-web defaults, stated here for
        // discoverability. Tune via VadRunnerOptions if recall/precision is off.
        positiveSpeechThreshold: 0.5,
        negativeSpeechThreshold: 0.35,
        minSpeechFrames: 3,
        redemptionFrames: 8,
        onSpeechStart: handlers.onSpeechStart,
        onSpeechEnd: handlers.onSpeechEnd,
      });
      if (destroyed) {
        instance.destroy();
        return;
      }
      micvad = instance;
    },
    start() {
      micvad?.start();
    },
    pause() {
      micvad?.pause();
    },
    async stop() {
      destroyed = true;
      micvad?.destroy();
      micvad = null;
    },
  };
}
